// The colour of an invoice, shared by the stripe of its cell in the list and the status line of
// its detail view: red while it is being prepared, yellow when something has to be fixed before
// it can be processed, green once it is processed.

enum InvoiceReviewState
{
    draft = 0,
    issues = 1,
    processed = 2
}

class InvoiceReviewStateHelper
{
    /// Posted with the invoice when a detail view finds or clears something to fix on it.
    static readonly didChangeNotification = "InvoiceReviewStateDidChange";

    static stateForInvoice(invoice:Invoice) : InvoiceReviewState {
        if (invoice == null) return InvoiceReviewState.draft;
        if (invoice.status == DBDocumentStatus.processed) return InvoiceReviewState.processed;
        // the review states: the document being read, a reading to go through, or one that failed
        let ocr = invoice.ocrStatus;
        if (ocr == DBDocumentOCRStatus.scanning || ocr == DBDocumentOCRStatus.extracted || ocr == DBDocumentOCRStatus.failed) return InvoiceReviewState.issues;
        // otherwise what the detail view found on the lines, when it has looked at the invoice
        return invoice.reviewHasIssues ? InvoiceReviewState.issues : InvoiceReviewState.draft;
    }

    /// The manager's status style: alert (red), caution (yellow), active (green).
    static styleForState(state:InvoiceReviewState) : string {
        switch (state) {
            case InvoiceReviewState.processed: return "active";
            case InvoiceReviewState.issues:    return "caution";
            default:                           return "alert";
        }
    }

    static titleForState(state:InvoiceReviewState) : string {
        switch (state) {
            case InvoiceReviewState.processed: return MIOLocalizeString("PROCESSED", "PROCESSED");
            case InvoiceReviewState.issues:    return MIOLocalizeString("WITH_ISSUES", "With issues");
            default:                           return MIOLocalizeString("NOT_PROCESSED", "Not processed");
        }
    }

    /// The stripe of a list cell: red while the invoice is being prepared, yellow in the review
    /// states, none once it is processed (the detail's status line goes green instead).
    static applyStripeToCell(layer:HTMLElement, state:InvoiceReviewState) {
        if (layer == null) return;
        for (let style of ["alert", "caution", "active"]) layer.classList.remove("stripe-" + style);
        if (state == InvoiceReviewState.processed) return;
        layer.classList.add("stripe-" + InvoiceReviewStateHelper.styleForState(state));
    }

    /// Puts the state's style on the layer (with the prefix), taking the other two off; the
    /// state's name as the tooltip when asked.
    static applyStateToLayer(layer:HTMLElement, state:InvoiceReviewState, prefix:string = "", withTitle:boolean = false) {
        if (layer == null) return;
        for (let style of ["alert", "caution", "active"]) layer.classList.remove(prefix + style);
        layer.classList.add(prefix + InvoiceReviewStateHelper.styleForState(state));
        if (withTitle) layer.title = InvoiceReviewStateHelper.titleForState(state);
    }
}
